import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import FlowFieldBackground from './FlowFieldBackground';

const EASE_REFINED = [0.22, 1, 0.36, 1];

export const DEFAULT_CTA = {
  headlineBefore: 'Protect your most valuable asset,',
  headlineAccent: 'your people.',
  body: 'Join leading companies using Stavyn to predict and prevent turnover. Request a free retention risk report for your team.',
  buttonLabel: 'Get Free Risk Report',
  buttonTo: '/schedule-demo',
};

const bandBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: 'Inter, DM Sans, sans-serif',
  fontWeight: 500,
  fontSize: '0.875rem',
  letterSpacing: '0.01em',
  color: 'var(--band-btn-text)',
  background: 'var(--band-btn-bg)',
  border: '1px solid var(--band-btn-bg)',
  padding: '0.75rem 1.75rem',
  borderRadius: 6,
  transition: 'all 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
  textDecoration: 'none',
  cursor: 'pointer',
};

function BandButton({ to, label }) {
  const isExternal = to.startsWith('http') || to.startsWith('mailto:');

  const hoverHandlers = {
    onMouseEnter: (e) => {
      e.currentTarget.style.background = 'var(--band-btn-hover)';
      e.currentTarget.style.borderColor = 'var(--band-btn-hover)';
      e.currentTarget.style.color = 'var(--band-btn-text)';
      e.currentTarget.style.transform = 'translateY(-1px)';
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.background = 'var(--band-btn-bg)';
      e.currentTarget.style.borderColor = 'var(--band-btn-bg)';
      e.currentTarget.style.color = 'var(--band-btn-text)';
      e.currentTarget.style.transform = 'translateY(0)';
    },
  };

  const arrow = (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path d="M2 6.5h9M8 3l3.5 3.5L8 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  if (isExternal) {
    return (
      <a href={to} style={bandBtnStyle} {...hoverHandlers}>
        {label}
        {arrow}
      </a>
    );
  }

  return (
    <Link to={to} style={bandBtnStyle} {...hoverHandlers}>
      {label}
      {arrow}
    </Link>
  );
}

export default function CTASection({
  embedded = false,
  headlineBefore = DEFAULT_CTA.headlineBefore,
  headlineAccent = DEFAULT_CTA.headlineAccent,
  body = DEFAULT_CTA.body,
  buttonLabel = DEFAULT_CTA.buttonLabel,
  buttonTo = DEFAULT_CTA.buttonTo,
}) {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-60px' });

  return (
    <motion.section
      ref={sectionRef}
      className={embedded ? 'cta-footer-bridge__cta' : undefined}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: EASE_REFINED }}
      style={{
        padding: '140px 0',
        position: 'relative',
        overflow: embedded ? 'visible' : 'hidden',
        background: embedded ? 'transparent' : 'var(--bg-base)',
        borderTop: embedded ? 'none' : '1px solid var(--border-divider)',
      }}
    >
      {!embedded && (
        <FlowFieldBackground gradientId="cta-flow-grad" preset="cta" fadeIn={false} />
      )}

      <div aria-hidden="true" className="cta-flow-legibility" />

      <div className={`max-w-5xl mx-auto px-8 lg:px-12 text-center relative${embedded ? ' z-[1]' : ' z-10'}`}>
        <motion.h2
          className="landing-heading"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.65, ease: EASE_REFINED, delay: 0.15 }}
          style={{
            fontSize: 'clamp(2.25rem, 5vw, 4.25rem)',
            lineHeight: 1.05,
            marginBottom: '2.5rem',
          }}
        >
          <span style={{ color: 'var(--band-text)' }}>{headlineBefore} </span>
          <span className="headline-accent band-headline-accent">{headlineAccent}</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, ease: EASE_REFINED, delay: 0.3 }}
          style={{
            fontFamily: 'DM Sans, Inter, sans-serif',
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: 1.75,
            color: 'var(--band-text-mid)',
            maxWidth: 480,
            margin: '0 auto 3.25rem',
          }}
        >
          {body}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: EASE_REFINED, delay: 0.42 }}
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <BandButton to={buttonTo} label={buttonLabel} />
        </motion.div>
      </div>
    </motion.section>
  );
}
